import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MeoSuggestion, MeoSuggestionMeta } from '@app/_interfaces/meo.interface';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { log } from '@app/_services/logger';

const COLLECTION_ROUTES: Record<string, string> = {
  todos: '/today',
  habitSetups: '/today',
  habitActions: '/today',
  notes: '/notes',
  lists: '/lists',
  journalSetups: '/today',
  journalActions: '/today',
  moneyTrackerSetups: '/today',
  moneyTrackerActions: '/today',
  focusSetups: '/today',
  focusActions: '/today',
  calendarEventSetups: '/today',
  calendarEventActions: '/today',
  userResources: '/today',
  viewSettings: '/settings',
  messageSetups: '/today',
  messageActions: '/today',
};

/**
 * Canonical collection-to-icon mapping for Meo suggestion cards.
 * Source of truth -- keep in sync with Flutter's MeoCollectionIcons
 * in lib/features/ai/utils/meo_collection_icons.dart.
 */
const COLLECTION_ICONS: Record<string, string> = {
  todos: 'todo',
  habitSetups: 'habit',
  habitActions: 'habit',
  notes: 'note',
  lists: 'list',
  journalSetups: 'journal',
  journalActions: 'journal',
  moneyTrackerSetups: 'moneyTracker',
  moneyTrackerActions: 'moneyTracker',
  focusSetups: 'focusTrainer',
  focusActions: 'focusTrainer',
  calendarEventSetups: 'calendar',
  calendarEventActions: 'calendar',
  userResources: 'feature',
  viewSettings: 'setting',
  messageSetups: 'chat',
  messageActions: 'chat',
};

const HABIT_TYPE_KEYS: Record<string, string> = {
  boolean: 'meo_habitTypeBoolean',
  numeric: 'meo_habitTypeNumeric',
  timer: 'meo_habitTypeTimer',
  single: 'meo_habitTypeSingleChoice',
  multiple: 'meo_habitTypeMultipleChoice',
};

/**
 * Canonical label-to-icon mapping for Meo suggestion card preview fields.
 * Labels come from extractPreviewFields() in meo.store.ts.
 * Source of truth -- keep in sync with Flutter's MeoCollectionIcons
 * in lib/features/ai/utils/meo_collection_icons.dart.
 */
const LABEL_ICON_MAP: Record<string, string> = {
  Date: 'calendar',
  Time: 'clock',
  Duration: 'timer',
  Repeat: 'repeat',
  Type: 'featureSetupType',
  Goal: 'target',
  Timer: 'timer',
  Description: 'description',
  Tags: 'hashtag',
  Items: 'listBullet',
  Currency: 'moneyTracker',
  Answer: 'circleCheck',
  Value: 'target',
  Entry: 'entry',
  Mood: 'journal',
  Amount: 'moneyTracker',
  Content: 'description',
  Email: 'mail',
  Role: 'collaborate',
};

interface CardRow {
  icon: string;
  value: string;
  rightIcon?: string;
  rightValue?: string;
  subText?: string;
}

@Component({
  selector: 'app-meo-suggestion-card',
  standalone: true,
  imports: [CommonModule, SvgComponent],
  templateUrl: './meo-suggestion-card.component.html',
  styleUrl: './meo-suggestion-card.component.scss',
})
export class MeoSuggestionCardComponent {
  readonly ss = inject(SettingsService);
  private readonly router = inject(Router);

  suggestionMeta = input.required<MeoSuggestionMeta>();
  suggestionEntity = input<MeoSuggestion | null>(null);

  confirmed = output<void>();
  dismissed = output<void>();

  get isPending(): boolean {
    return this.suggestionMeta().outcome === 'pending';
  }

  get isConfirmed(): boolean {
    return this.suggestionMeta().outcome === 'confirmed';
  }

  get isDismissed(): boolean {
    return this.suggestionMeta().outcome === 'dismissed';
  }

  get isExpired(): boolean {
    return this.suggestionMeta().outcome === 'expired';
  }

  get capitalizedTitle(): string {
    const title = this.suggestionMeta().title || '';
    return title.charAt(0).toUpperCase() + title.slice(1);
  }

  get entityTypeLabel(): string {
    const collection = this.suggestionMeta().collection;
    const translatedLabels: Record<string, string> = {
      todos: this.ss.t('meo_collection_todo'),
      habitSetups: this.ss.t('meo_collection_habit'),
      habitActions: this.ss.t('meo_collection_habitEntry'),
      notes: this.ss.t('meo_collection_note'),
      lists: this.ss.t('meo_collection_list'),
      journalSetups: this.ss.t('meo_collection_journal'),
      journalActions: this.ss.t('meo_collection_journalEntry'),
      moneyTrackerSetups: this.ss.t('meo_collection_moneyTracker'),
      moneyTrackerActions: this.ss.t('meo_collection_transaction'),
      focusSetups: this.ss.t('meo_collection_focus'),
      focusActions: this.ss.t('meo_collection_focusSession'),
      calendarEventSetups: this.ss.t('meo_collection_calendarEvent'),
      calendarEventActions: this.ss.t('meo_collection_calendarEvent'),
      userResources: this.ss.t('meo_collection_memory'),
      viewSettings: this.ss.t('meo_collection_settings'),
      messageSetups: this.ss.t('meo_collection_message'),
      messageActions: this.ss.t('meo_collection_message'),
    };
    return translatedLabels[collection] || collection;
  }

  get entityIconName(): string {
    const action = this.suggestionMeta().action;
    // Use action-specific icons for collaboration/messaging
    if (action === 'addCollaborator' || action === 'removeCollaborator' || action === 'updateCollaboratorRole') {
      return 'collaborate';
    }
    if (action === 'sendMessage') {
      return 'chat';
    }
    return COLLECTION_ICONS[this.suggestionMeta().collection] || 'feature';
  }

  get cardRows(): CardRow[] {
    const entity = this.suggestionEntity()?.entity;
    if (entity) {
      return this._buildRowsFromEntity(entity);
    }
    return this._buildRowsFromPreviewFields();
  }

  get actionButtonText(): string {
    const action = this.suggestionMeta().action;
    const type = this.entityTypeLabel;
    switch (action) {
      case 'create':
        return `${this.ss.t('meo_create_action')} ${type}`;
      case 'update':
        return `${this.ss.t('meo_update_action')} ${type}`;
      case 'delete':
        return `${this.ss.t('meo_delete_action')} ${type}`;
      case 'complete':
        return `${this.ss.t('meo_complete_action')} ${type}`;
      case 'addCollaborator':
        return this.ss.t('meo_confirm_share');
      case 'removeCollaborator':
        return this.ss.t('meo_confirm_remove');
      case 'updateCollaboratorRole':
        return `${this.ss.t('meo_update_action')} Role`;
      case 'sendMessage':
        return this.ss.t('meo_confirm_send');
      default:
        return `${this.ss.t('meo_update_action')} ${type}`;
    }
  }

  get confirmedBadgeText(): string {
    const type = this.entityTypeLabel;
    const action = this.suggestionMeta().action;
    switch (action) {
      case 'create':
        return `${type} ${this.ss.t('meo_created_badge')}`;
      case 'update':
        return `${type} ${this.ss.t('meo_updated_badge')}`;
      case 'delete':
        return `${type} ${this.ss.t('meo_deleted_badge')}`;
      case 'complete':
        return `${type} ${this.ss.t('meo_completed_badge')}`;
      case 'addCollaborator':
        return this.ss.t('meo_shared_badge');
      case 'removeCollaborator':
        return this.ss.t('meo_removed_badge');
      case 'updateCollaboratorRole':
        return this.ss.t('meo_updated_badge');
      case 'sendMessage':
        return this.ss.t('meo_sent_badge');
      default:
        return `${type} ${this.ss.t('meo_created_badge')}`;
    }
  }

  onConfirm(): void {
    this.confirmed.emit();
  }

  onDismiss(): void {
    this.dismissed.emit();
  }

  onBadgeClick(): void {
    const collection = this.suggestionMeta().collection;
    const route = COLLECTION_ROUTES[collection] || '/today';
    this.router.navigate([route]);
    log.info('MeoSuggestionCard', 'Badge clicked, navigating', { collection, route });
  }

  // ==================== Private Helpers ====================

  private _buildRowsFromPreviewFields(): CardRow[] {
    const fields = this.suggestionMeta().previewFields || [];
    return fields.map(f => ({
      icon: LABEL_ICON_MAP[f.label] || 'note',
      value: f.label === 'Type' ? (this.ss.t(HABIT_TYPE_KEYS[f.value] || '') || f.value) : f.value,
    }));
  }

  private _buildRowsFromEntity(entity: Record<string, unknown>): CardRow[] {
    const rows: CardRow[] = [];
    const collection = this.suggestionMeta().collection;
    const action = this.suggestionMeta().action;

    // Handle collaboration actions — these use delta fields (_op, _email, _role)
    // regardless of which collection the target entity belongs to
    if (action === 'addCollaborator' || action === 'removeCollaborator' || action === 'updateCollaboratorRole') {
      if (entity['_email']) {
        rows.push({ icon: 'collaborate', value: String(entity['_email']) });
      }
      if (action === 'addCollaborator' && entity['_role']) {
        const roleLabel = entity['_role'] === 'viewer'
          ? (this.ss.t('meo_role_viewer'))
          : (this.ss.t('meo_role_editor'));
        rows.push({ icon: 'feature', value: roleLabel });
      }
      if (action === 'updateCollaboratorRole') {
        const oldRole = entity['_oldRole'] === 'viewer'
          ? (this.ss.t('meo_role_viewer'))
          : (this.ss.t('meo_role_editor'));
        const newRole = entity['_newRole'] === 'viewer'
          ? (this.ss.t('meo_role_viewer'))
          : (this.ss.t('meo_role_editor'));
        rows.push({ icon: 'feature', value: `${oldRole} \u2192 ${newRole}` });
      }
      return rows;
    }

    // Handle sendMessage action — uses delta fields (_messageText, _recipientEmail, etc.)
    if (action === 'sendMessage') {
      if (entity['_messageText']) {
        const text = String(entity['_messageText']);
        rows.push({ icon: 'chat', value: text.length > 100 ? text.substring(0, 100) + '...' : text });
      }
      if (entity['_recipientEmail']) {
        rows.push({ icon: 'collaborate', value: String(entity['_recipientEmail']) });
      }
      if (entity['_recipientEmails'] && Array.isArray(entity['_recipientEmails'])) {
        rows.push({ icon: 'collaborate', value: (entity['_recipientEmails'] as string[]).join(', ') });
      }
      return rows;
    }

    switch (collection) {
      case 'todos': {
        if (entity['startDate']) {
          rows.push({ icon: 'calendar', value: String(entity['startDate']) });
        }
        if (entity['startTime'] && entity['startTime'] !== '00:00') {
          const timeRow: CardRow = { icon: 'clock', value: String(entity['startTime']) };
          if (entity['duration'] && Number(entity['duration']) > 0) {
            const mins = Number(entity['duration']);
            const display = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
            timeRow.rightIcon = 'timer';
            timeRow.rightValue = display;
          }
          if (entity['timezone']) {
            timeRow.subText = String(entity['timezone']);
          }
          rows.push(timeRow);
        } else if (entity['duration'] && Number(entity['duration']) > 0) {
          const mins = Number(entity['duration']);
          const display = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
          rows.push({ icon: 'timer', value: display });
        }
        this._addRepeatRow(entity, rows);
        break;
      }

      case 'habitSetups':
        if (entity['habitType']) {
          const raw = String(entity['habitType']);
          rows.push({ icon: 'featureSetupType', value: this.ss.t(HABIT_TYPE_KEYS[raw] || '') || raw });
        }
        this._addRepeatRow(entity, rows);
        if (entity['numericGoal']) {
          const unit = entity['numericUnit'] ? ` ${entity['numericUnit']}` : '';
          rows.push({ icon: 'target', value: `${entity['numericGoal']}${unit}` });
        }
        if (entity['timerGoal']) {
          rows.push({ icon: 'timer', value: String(entity['timerGoal']) });
        }
        break;

      case 'notes':
        if (entity['description']) {
          const desc = this._stripMarkdown(String(entity['description']));
          rows.push({ icon: 'note', value: desc.length > 100 ? desc.substring(0, 100) + '...' : desc });
        }
        break;

      case 'lists':
        if (entity['hashtags'] && Array.isArray(entity['hashtags']) && entity['hashtags'].length > 0) {
          rows.push({ icon: 'hashtag', value: `${entity['hashtags'].length}` });
        }
        if (entity['listItems'] && typeof entity['listItems'] === 'object') {
          // listItems is a map { id: { item, done, ... } }, not an array
          const itemCount = Object.keys(entity['listItems']).length;
          if (itemCount > 0) {
            rows.push({ icon: 'listBullet', value: `${itemCount} ${this.ss.t('meo_items_count')}` });
          }
        }
        break;

      case 'journalSetups':
        this._addRepeatRow(entity, rows);
        break;

      case 'moneyTrackerSetups':
        if (entity['currencyCode']) {
          rows.push({ icon: 'moneyTracker', value: String(entity['currencyCode']) });
        }
        break;

      case 'focusSetups':
        this._addDurationRow(entity, rows);
        this._addRepeatRow(entity, rows);
        break;

      case 'habitActions':
        if (entity['booleanAnswer'] != null) {
          rows.push({ icon: 'habit', value: entity['booleanAnswer'] ? this.ss.t('common_yes') : this.ss.t('common_no') });
        }
        if (entity['numericAnswer'] && Array.isArray(entity['numericAnswer']) && entity['numericAnswer'].length > 0) {
          const firstEntry = entity['numericAnswer'][0] as Record<string, unknown>;
          rows.push({ icon: 'target', value: String(firstEntry?.['value'] ?? '') });
        }
        if (entity['timerAnswer'] && Array.isArray(entity['timerAnswer']) && entity['timerAnswer'].length > 0) {
          rows.push({ icon: 'timer', value: this.ss.t('meo_completed_label') });
        }
        break;

      case 'journalActions':
        if (entity['description']) {
          const desc = this._stripMarkdown(String(entity['description']));
          rows.push({ icon: 'note', value: desc.length > 100 ? desc.substring(0, 100) + '...' : desc });
        }
        if (entity['emotion'] != null) {
          const emotions = [
            this.ss.t('meo_emotion_terrible'),
            this.ss.t('meo_emotion_bad'),
            this.ss.t('meo_emotion_okay'),
            this.ss.t('meo_emotion_good'),
            this.ss.t('meo_emotion_great'),
          ];
          rows.push({ icon: 'journal', value: emotions[Number(entity['emotion'])] || String(entity['emotion']) });
        }
        break;

      case 'moneyTrackerActions':
        if (entity['amount'] !== undefined) {
          const txnType = entity['transactionType'] === 'income' ? '+' : '-';
          rows.push({ icon: 'moneyTracker', value: `${txnType}${entity['amount']}` });
        }
        if (entity['dueDate']) {
          rows.push({ icon: 'calendar', value: String(entity['dueDate']) });
        }
        break;

      case 'focusActions':
        this._addDurationRow(entity, rows);
        break;

      case 'calendarEventSetups':
      case 'calendarEventActions': {
        if (entity['startDate']) {
          rows.push({ icon: 'calendar', value: String(entity['startDate']) });
        }
        if (entity['startTime'] && entity['startTime'] !== '00:00') {
          rows.push({ icon: 'clock', value: String(entity['startTime']) });
        }
        this._addDurationRow(entity, rows);
        break;
      }

      case 'userResources':
        if (entity['content']) {
          const content = String(entity['content']);
          rows.push({ icon: 'note', value: content.length > 100 ? content.substring(0, 100) + '...' : content });
        }
        break;

      case 'viewSettings': {
        // Show a summary of the settings that changed
        const appSettings = entity['appSettings'] as Record<string, unknown> | undefined;
        if (appSettings?.['themeColor']) {
          const colorKeys: Record<string, string> = { a: 'theme_colorGreenTeal', b: 'theme_colorWarmBrown', c: 'theme_colorIndigoBlue', d: 'theme_colorOliveGreen', e: 'theme_colorGolden', f: 'theme_colorTealBlue', g: 'theme_colorRosePink', h: 'theme_colorForestGreen', i: 'theme_colorSkyBlue', j: 'theme_colorPurple' };
          rows.push({ icon: 'feature', value: this.ss.t(colorKeys[String(appSettings['themeColor'])] || '') || String(appSettings['themeColor']) });
        }
        if (appSettings?.['appTheme']) {
          rows.push({ icon: 'feature', value: String(appSettings['appTheme']) });
        }
        if (appSettings?.['language']) {
          rows.push({ icon: 'feature', value: String(appSettings['language']).toUpperCase() });
        }
        const mtSettings = entity['moneyTrackerSettings'] as Record<string, unknown> | undefined;
        const mtConfig = mtSettings?.['config'] as Record<string, unknown> | undefined;
        if (mtConfig?.['currencyCode']) {
          rows.push({ icon: 'moneyTracker', value: String(mtConfig['currencyCode']) });
        }
        break;
      }

      default:
        log.warn('MeoSuggestionCardComponent._buildRowsFromEntity', `Unknown collection: ${collection}`, { collection });
        break;
    }

    return rows;
  }

  private _addDurationRow(entity: Record<string, unknown>, rows: CardRow[]): void {
    if (entity['duration'] && Number(entity['duration']) > 0) {
      const mins = Number(entity['duration']);
      const display = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      rows.push({ icon: 'timer', value: display });
    }
  }

  private _stripMarkdown(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')  // **bold** → bold
      .replace(/\*(.*?)\*/g, '$1')      // *italic* → italic
      .replace(/~~(.*?)~~/g, '$1')      // ~~strike~~ → strike
      .replace(/`(.*?)`/g, '$1')        // `code` → code
      .replace(/#{1,6}\s/g, '')         // ### heading → heading
      .replace(/>\s/g, '')              // > quote → quote
      .trim();
  }

  private _addRepeatRow(entity: Record<string, unknown>, rows: CardRow[]): void {
    const repeat = entity['repeat'] as string | null;
    if (repeat) {
      let display = repeat;
      if (repeat.includes('DAILY')) {
        display = this.ss.t('meo_repeat_daily');
      } else if (repeat.includes('WEEKLY')) {
        // Check for weekdays (MO,TU,WE,TH,FR)
        if (repeat.includes('BYDAY=MO,TU,WE,TH,FR')) {
          display = this.ss.t('meo_repeat_weekdays');
        } else if (repeat.includes('BYDAY=')) {
          // Extract and display specific days
          const byDay = repeat.match(/BYDAY=([A-Z,]+)/)?.[1] || '';
          display = byDay;
        } else {
          display = this.ss.t('meo_repeat_weekly');
        }
      } else if (repeat.includes('MONTHLY')) {
        display = this.ss.t('meo_repeat_monthly');
      }
      rows.push({ icon: 'repeat', value: display });
    }
  }
}
